import { DATE_FORMAT, INTEGRATIONS } from 'utils/constants'
import {
  OPTIONS,
  YEAR_FORMAT_OPTIONS,
  MONTH_FORMAT_OPTIONS,
  RESET_EVERY_OPTIONS
} from './Opportunity/QuoteFormats/constants'

export const toOrganization = organization => {
  const {
    id = '',
    name = '',
    shortName = '',
    departments = [],
    email = '',
    interest = '',
    size = '',
    type = '',
    currency = {},
    timezone = '',
    dateFormat,
    phone = '',
    address = '',
    address2 = '',
    street2 = '',
    city = '',
    state = '',
    zipcode = '',
    emailIntegration = '',
    emailSettings = null,
    dataExportSettings = null,
    dealSettings = null,
    numberAutomations = [],
    createdAt = null,
    emailTC = null,
    stripeId = ''
  } = organization
  return {
    departments: toDepartments(departments),
    email: email || shortName + '@idealemail.app',
    shortName,
    id,
    interest,
    name,
    size,
    type,
    timezone: timezone ? { value: timezone, label: timezone } : '',
    currency: {
      value: currency.id,
      label: `${currency.description} (${currency.iso})`
    },
    dateFormat: {
      value: dateFormat,
      label: DATE_FORMAT[dateFormat]
    },
    phone,
    address,
    address2,
    street2,
    city,
    state,
    zipcode,
    dataExportSettings: dataExportSettings || {
      enabled: false,
      recipients: null
    },
    emailSettings: emailSettings
      ? {
          ...emailSettings,
          mode: INTEGRATIONS.CONNECTION_MODE.find(m => m.value === emailSettings.mode)
        }
      : {
          name: '',
          domain: '',
          port: '',
          mode: null,
          includeDefaultEmailFooter: false
        },
    emailIntegration,
    emailTC: {
      active: emailTC.active,
      content: emailTC.content
    },
    dealSettings: {
      sendApprovalEmail: dealSettings.sendApprovalEmail === 'USER_ENABLED' ? true : false,
      wonDealEmail: dealSettings.wonDealEmail === 'USER_ENABLED' ? true : false,
      checkOpportunityHealth: dealSettings.checkOpportunityHealth === 'USER_ENABLED' ? true : false,
      opportunityHealthManualOverride:
        dealSettings.opportunityHealthManualOverride === 'USER_ENABLED' ? true : false,
      showPublicPrivate: dealSettings.showPublicPrivate,
      showProjectStatus: dealSettings.showProjectStatus,
      showInvitedToBid: dealSettings.showInvitedToBid,
      teamAddEmail: dealSettings.teamAddEmail === 'USER_ENABLED' ? true : false,
      filters: dealSettings.filters,
      projectNumberDealStatuses: dealSettings.projectNumberDealStatuses
    },
    numberAutomations: numberAutomations.map(n => {
      const {
        id,
        automationType,
        automationValue,
        firstText,
        monthFormat,
        resetType,
        secondText,
        startNumber,
        thirdText,
        yearFormat,
        fifthOptionType,
        fifthSeparator,
        firstOptionType,
        firstSeparator,
        fourthOptionType,
        fourthSeparator,
        secondOptionType,
        secondSeparator,
        sixthOptionType,
        thirdOptionType,
        thirdSeparator,
        currentNumber
      } = n
      return {
        id,
        automationType,
        automationValue: automationValue === 'USER_ENABLED' ? true : false,
        firstText,
        monthFormat: monthFormat ? MONTH_FORMAT_OPTIONS.find(o => o.value === monthFormat) : null,
        resetType: resetType ? RESET_EVERY_OPTIONS.find(o => o.value === resetType) : null,
        secondText,
        startNumber,
        thirdText,
        yearFormat: yearFormat ? YEAR_FORMAT_OPTIONS.find(o => o.value === yearFormat) : null,
        fifthOptionType: fifthOptionType ? OPTIONS.find(o => o.value === fifthOptionType) : null,
        fifthSeparator,
        firstOptionType: firstOptionType ? OPTIONS.find(o => o.value === firstOptionType) : null,
        firstSeparator,
        fourthOptionType: fourthOptionType ? OPTIONS.find(o => o.value === fourthOptionType) : null,
        fourthSeparator,
        secondOptionType: secondOptionType ? OPTIONS.find(o => o.value === secondOptionType) : null,
        secondSeparator,
        sixthOptionType: sixthOptionType ? OPTIONS.find(o => o.value === sixthOptionType) : null,
        thirdOptionType: thirdOptionType ? OPTIONS.find(o => o.value === thirdOptionType) : null,
        thirdSeparator,
        currentNumber
      }
    }),
    registeredOn: createdAt,
    stripeId
  }
}

export const toDepartments = departments =>
  departments.map(department => ({
    value: department.id,
    label: department.name
  }))

export const fromUserDetails = user => {
  const {
    cellPhone,
    department,
    email,
    firstName,
    lastName,
    password,
    phone,
    validateRoles,
    organizationId,
    teams,
    yearlyQuota,
    monthlyQuota,
    monthlyTargetProfit,
    yearlyTargetProfit
  } = user
  return {
    cellPhone,
    departmentId: department?.value,
    email,
    firstName,
    lastName,
    organizationId,
    password,
    phone,
    roles: validateRoles.map(role => role.value),
    teams: teams.map(team => team.value),
    yearlyQuota,
    monthlyQuota,
    monthlyTargetProfit,
    yearlyTargetProfit
  }
}

export const fromOrganization = organization => {
  const {
    departments,
    email,
    shortName,
    id,
    interest,
    name,
    size,
    type,
    timezone,
    dateFormat,
    phone,
    address,
    address2,
    street2,
    city,
    state,
    zipcode,
    emailSettings,
    emailIntegration,
    emailTC,
    dealSettings,
    dataExportSettings
  } = organization
  return {
    departments: departments.map(d => d.label),
    email,
    shortName,
    id,
    phone,
    interest,
    name,
    size,
    type,
    currencyId: organization?.currency?.value,
    timezone: timezone?.value,
    dateFormat: dateFormat?.value,
    address,
    address2,
    street2,
    city,
    state,
    zipcode,
    emailSettings: fromEmailSettings(emailSettings, emailIntegration),
    emailIntegration,
    emailTC,
    dealSettings: {
      sendApprovalEmail: dealSettings.sendApprovalEmail ? 'USER_ENABLED' : 'USER_DISABLED',
      wonDealEmail: dealSettings.wonDealEmail ? 'USER_ENABLED' : 'USER_DISABLED',
      checkOpportunityHealth: dealSettings.checkOpportunityHealth
        ? 'USER_ENABLED'
        : 'USER_DISABLED',
      opportunityHealthManualOverride: dealSettings.opportunityHealthManualOverride
        ? 'USER_ENABLED'
        : 'USER_DISABLED',
      showPublicPrivate: dealSettings.showPublicPrivate,
      showProjectStatus: dealSettings.showProjectStatus,
      showInvitedToBid: dealSettings.showInvitedToBid,
      teamAddEmail: dealSettings.teamAddEmail ? 'USER_ENABLED' : 'USER_DISABLED',
      filters: dealSettings.filters,
      projectNumberDealStatuses: dealSettings.projectNumberDealStatuses
    },
    dataExportSettings: {
      enabled: dataExportSettings.enabled,
      recipients: dataExportSettings.recipients
    },
    numberAutomations: organization.numberAutomations.map(n => ({
      id: n.id,
      automationType: n.automationType,
      automationValue: n.automationValue ? 'USER_ENABLED' : 'USER_DISABLED',
      firstText: n.firstText,
      monthFormat: n.monthFormat?.value ?? null,
      resetType: n.resetType?.value ?? null,
      secondText: n.secondText,
      startNumber: n.startNumber,
      thirdText: n.thirdText,
      yearFormat: n.yearFormat?.value ?? null,
      fifthOptionType: n.fifthOptionType?.value ?? null,
      fifthSeparator: n.fifthSeparator,
      firstOptionType: n.firstOptionType?.value ?? null,
      firstSeparator: n.firstSeparator,
      fourthOptionType: n.fourthOptionType?.value ?? null,
      fourthSeparator: n.fourthSeparator,
      secondOptionType: n.secondOptionType?.value ?? null,
      secondSeparator: n.secondSeparator,
      sixthOptionType: n.sixthOptionType?.value ?? null,
      thirdOptionType: n.thirdOptionType?.value ?? null,
      thirdSeparator: n.thirdSeparator
    }))
  }
}

const fromEmailSettings = (emailSettings, emailIntegration) => {
  return {
    ...emailSettings,
    mode: [INTEGRATIONS.MAIL.OFFICE365, INTEGRATIONS.MAIL.IDEAL].includes(emailIntegration)
      ? null
      : emailSettings?.mode?.value
  }
}
